/* eslint-disable no-restricted-globals */
"use client";

import { Input, OutputTransform } from "@noya-app/noya-schemas";
import { Base64, memoize, uuid } from "@noya-app/noya-utils";
import { downloadBlob, memoGeneric } from "@noya-app/react-utils";
import { NoyaAsset, NoyaManager } from "@noya-app/state-manager";
import React, {
  CSSProperties,
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { ObjectInspector } from "react-inspector";
import { useManagedHistory } from "../hooks";
import { useObservable } from "../useObservable";
import { ColoredDot } from "./ColoredDot";
import {
  getStateInspectorBorderColor,
  getStateInspectorTheme,
} from "./inspectorTheme";
import { ActivityEventsSection } from "./sections/ActivityEventsSection";
import { EventsSection } from "./sections/EventsSection";
import { GitSection } from "./sections/GitSection";
import { HistorySection } from "./sections/HistorySection";
import { ServerScriptLogsSection } from "./sections/ServerScriptLogsSection";
import { exportAll, importAll } from "./serialization";
import { StateInspectorButton } from "./StateInspectorButton";
import {
  StateInspectorDisclosureRowInner,
  StateInspectorDisclosureSection,
} from "./StateInspectorDisclosureSection";
import { StateInspectorRow } from "./StateInspectorRow";
import { StateInspectorTitleLabel } from "./StateInspectorTitleLabel";
import { StateInspectorToggleButton } from "./StateInspectorToggleButton";
import { useLocalStorageState } from "./useLocalStorageState";
import { usePausedStream } from "./usePausedStream";
import { ellipsis, uploadFile } from "./utils";

export type StateInspectorAnchor =
  | "left"
  | "right"
  | "bottom left"
  | "bottom right";

export type StateInspectorOptions = {
  colorScheme?: "light" | "dark";
  anchor?: StateInspectorAnchor;
  advanced?: boolean;
  defaultCollapsed?: boolean;
};

export const StateInspector = memoGeneric(function StateInspector<
  S,
  M extends object,
  E extends object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
>({
  noyaManager,
  colorScheme = "light",
  anchor = "right",
  unstyled = false,
  advanced = false,
  defaultCollapsed = false,
  ...props
}: {
  noyaManager: NoyaManager<S, M, E, MenuT, I>;
  unstyled?: boolean;
} & StateInspectorOptions &
  ComponentPropsWithoutRef<"div">) {
  const {
    multiplayerStateManager,
    assetManager,
    userManager,
    secretManager,
    sharedConnectionDataManager,
    connectionEventManager,
    taskManager,
    ioManager,
    resourceManager,
    activityEventsManager,
    logManager,
    gitManager,
  } = noyaManager;

  const [didMount, setDidMount] = React.useState(false);
  const tasks = useObservable(taskManager.tasks$);
  const inputs = useObservable(ioManager.inputs$);
  const outputTransforms = useObservable(ioManager.outputTransforms$);

  useLayoutEffect(() => {
    setDidMount(true);
  }, []);

  const eventsContainerRef = React.useRef<HTMLDivElement>(null);
  const activityEventsContainerRef = React.useRef<HTMLDivElement>(null);
  const serverLogsContainerRef = React.useRef<HTMLDivElement>(null);
  const serverSimulationLogsContainerRef = React.useRef<HTMLDivElement>(null);

  const [showInspector, setShowInspector] = useLocalStorageState(
    "noya-multiplayer-react-show-inspector",
    !defaultCollapsed
  );
  const [showEvents, setShowEvents] = useLocalStorageState(
    "noya-multiplayer-react-show-events",
    false
  );
  const [showHistory, setShowHistory] = useLocalStorageState(
    "noya-multiplayer-react-show-history",
    false
  );
  const [showUsers, setShowUsers] = useLocalStorageState(
    "noya-multiplayer-react-show-users",
    true
  );
  const [showUserDetails, setShowUserDetails] = useLocalStorageState(
    "noya-multiplayer-react-show-user-details",
    false
  );
  const [showData, setShowData] = useLocalStorageState(
    "noya-multiplayer-react-show-data",
    true
  );
  const [showTasks, setShowTasks] = useLocalStorageState(
    "noya-multiplayer-react-show-tasks",
    false
  );
  const [showShared, setShowShared] = useLocalStorageState(
    "noya-multiplayer-react-show-shared",
    false
  );
  const [showAssets, setShowAssets] = useLocalStorageState(
    "noya-multiplayer-react-show-assets",
    false
  );
  const [showResources, setShowResources] = useLocalStorageState(
    "noya-multiplayer-react-show-resources",
    false
  );
  const [showSecrets, setShowSecrets] = useLocalStorageState(
    "noya-multiplayer-react-show-secrets",
    false
  );
  const [showInputs, setShowInputs] = useLocalStorageState(
    "noya-multiplayer-react-show-inputs",
    false
  );
  const [showOutputTransforms, setShowOutputTransforms] = useLocalStorageState(
    "noya-multiplayer-react-show-output-transforms",
    false
  );
  const [showActivityEvents, setShowActivityEvents] = useLocalStorageState(
    "noya-multiplayer-react-show-activity-events",
    false
  );
  const [showServerLogs, setShowServerLogs] = useLocalStorageState(
    "noya-multiplayer-react-show-server-logs",
    false
  );
  const [showServerSimulationLogs, setShowServerSimulationLogs] =
    useLocalStorageState(
      "noya-multiplayer-react-show-server-simulation-logs",
      false
    );
  const [showGit, setShowGit] = useLocalStorageState(
    "noya-multiplayer-react-show-git",
    false
  );
  const [showSimulation, setShowSimulation] = useLocalStorageState(
    "noya-multiplayer-react-show-simulation",
    false
  );

  const connectionEvents = useObservable(connectionEventManager.events$);
  const {
    visibleItems: visibleConnectionEvents,
    paused: connectionEventsPaused,
    togglePaused: toggleConnectionEventsPaused,
  } = usePausedStream(connectionEvents);

  useEffect(() => {
    if (eventsContainerRef.current) {
      eventsContainerRef.current.scrollTop =
        eventsContainerRef.current.scrollHeight;
    }
  }, [visibleConnectionEvents]);

  const serverLogs = useObservable(logManager.logs$);
  const {
    visibleItems: visibleServerLogs,
    paused: serverLogsPaused,
    togglePaused: toggleServerLogsPaused,
  } = usePausedStream(serverLogs);
  const serverSimulationLogs = useObservable(
    multiplayerStateManager.serverSimulationLogs$
  );
  const {
    visibleItems: visibleServerSimulationLogs,
    paused: serverSimulationLogsPaused,
    togglePaused: toggleServerSimulationLogsPaused,
  } = usePausedStream(serverSimulationLogs);

  useEffect(() => {
    if (!showServerLogs) return;
    if (serverLogsContainerRef.current) {
      serverLogsContainerRef.current.scrollTop =
        serverLogsContainerRef.current.scrollHeight;
    }
  }, [showServerLogs, visibleServerLogs]);
  useEffect(() => {
    if (!showServerSimulationLogs) return;
    if (serverSimulationLogsContainerRef.current) {
      serverSimulationLogsContainerRef.current.scrollTop =
        serverSimulationLogsContainerRef.current.scrollHeight;
    }
  }, [showServerSimulationLogs, visibleServerSimulationLogs]);

  const multipeerStateInitialized = useObservable(
    multiplayerStateManager.isInitialized$
  );
  const shared = useObservable(sharedConnectionDataManager.data$);
  const historySnapshot = useManagedHistory(multiplayerStateManager.sm);
  const assets = useObservable(assetManager.assets$);
  const resources = useObservable(resourceManager.resources$);
  const resourcesInitialized = useObservable(resourceManager.isInitialized$);
  const assetsInitialized = useObservable(assetManager.isInitialized$);
  const secrets = useObservable(secretManager.secrets$);
  const secretsInitialized = useObservable(secretManager.isInitialized$);
  const connectedUsers = useObservable(userManager.allUsers$);
  const connectionId = useObservable(userManager.currentConnectionId$);
  const simulationDiagnostics =
    multiplayerStateManager.getServerSimulationDiagnostics?.() ?? null;
  const showSimulationLogsSection =
    (simulationDiagnostics?.simulateLocally ?? false) ||
    visibleServerSimulationLogs.length > 0;
  const state = useObservable(multiplayerStateManager.optimisticState$);
  const inputsInitialized = useObservable(ioManager.inputsInitialized$);
  const outputTransformsInitialized = useObservable(
    ioManager.outputTransformsInitialized$
  );
  const theme = getStateInspectorTheme(colorScheme);
  const solidBorderColor = getStateInspectorBorderColor(colorScheme);

  const baseStyle: CSSProperties = {
    position: "fixed",
    top: 12,
    ...(anchor.includes("right") ? { right: 12 } : { left: 12 }),
    bottom: 12,
    width: 400,
    background:
      colorScheme === "light" ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.95)",
    backdropFilter: "blur(48px)",
    border: `1px solid ${solidBorderColor}`,
    // outlineOffset: -1,
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    // gap: 12,
    color: theme.BASE_COLOR,
    overflow: "hidden",
    zIndex: 9999,
    lineHeight: "13px",
  };

  const handleExportAll = useCallback(async () => {
    const buffer = await exportAll(noyaManager);

    downloadBlob(new Blob([buffer]), "state.zip");
  }, [noyaManager]);

  const handleImportAll = useCallback(async () => {
    const file = await uploadFile();
    const buffer = await file.arrayBuffer();

    importAll(buffer, noyaManager, {
      shouldAllowSchemaChange: () => {
        return confirm(
          `The imported state uses a different schema than the schema this app expects. Do you want to continue?`
        );
      },
    });
  }, [noyaManager]);

  if (!didMount) return null;

  if (!showInspector) {
    return (
      <div
        {...props}
        style={{
          ...baseStyle,
          padding: "4px 10px",
          width: undefined,
          ...(anchor.includes("bottom")
            ? { bottom: 12, top: undefined }
            : { bottom: undefined }),
        }}
        onClick={() => setShowInspector(true)}
      >
        <StateInspectorToggleButton
          showInspector={showInspector}
          setShowInspector={setShowInspector}
          theme={theme}
          anchor={anchor}
        />
      </div>
    );
  }

  return (
    <div
      {...props}
      style={{
        ...(!unstyled && baseStyle),
        ...props.style,
      }}
    >
      <StateInspectorDisclosureSection
        isFirst
        open={showUsers}
        setOpen={setShowUsers}
        title="Users"
        colorScheme={colorScheme}
        style={{
          flex: "0 0 auto",
          maxHeight: "50%",
          overflowY: "auto",
        }}
        right={
          <span style={{ display: "flex", gap: "10px" }}>
            <StateInspectorButton
              theme={theme}
              onClick={() => setShowUserDetails(!showUserDetails)}
            >
              Toggle details
            </StateInspectorButton>
            <StateInspectorToggleButton
              showInspector={showInspector}
              setShowInspector={setShowInspector}
              theme={theme}
              anchor={anchor}
            />
          </span>
        }
      >
        <StateInspectorDisclosureRowInner style={{ flex: "0 0 auto" }}>
          {connectedUsers
            ?.filter((user) => showUserDetails || !user.inactive)
            .map((user, index, array) => {
              return (
                <StateInspectorRow
                  key={user.connectionId}
                  colorScheme={colorScheme}
                  variant={
                    user.connectionId === connectionId ? "up" : undefined
                  }
                  bordered={index !== array.length - 1}
                  style={{
                    opacity: user.inactive ? 0.5 : 1,
                  }}
                >
                  {user.image && (
                    <img
                      src={user.image}
                      alt={user.name}
                      style={{
                        width: "13px",
                        height: "13px",
                        borderRadius: "50%",
                        marginRight: "4px",
                        display: "inline-block",
                        position: "relative",
                        background: solidBorderColor,
                        verticalAlign: "middle",
                      }}
                    />
                  )}
                  {showUserDetails ? (
                    <ObjectInspector data={user} theme={theme} />
                  ) : (
                    user.name
                  )}
                </StateInspectorRow>
              );
            })}
          {!connectedUsers && (
            <div
              style={{
                padding: "12px",
                fontSize: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <span>No connected users</span>
            </div>
          )}
        </StateInspectorDisclosureRowInner>
      </StateInspectorDisclosureSection>
      <StateInspectorDisclosureSection
        title={
          <StateInspectorTitleLabel>
            Data
            <ColoredDot
              type={multipeerStateInitialized ? "success" : "error"}
            />
          </StateInspectorTitleLabel>
        }
        colorScheme={colorScheme}
        setOpen={setShowData}
        open={showData}
        right={
          <span
            style={{
              display: "flex",
              gap: "12px",
            }}
          >
            <StateInspectorButton theme={theme} onClick={handleImportAll}>
              Import
            </StateInspectorButton>
            <StateInspectorButton theme={theme} onClick={handleExportAll}>
              Export
            </StateInspectorButton>
            <StateInspectorButton
              // disabled={!historySnapshot.canUndo}
              theme={theme}
              onClick={() => {
                noyaManager.forceInit();
              }}
            >
              Reset
            </StateInspectorButton>
          </span>
        }
      >
        <StateInspectorDisclosureRowInner>
          <StateInspectorRow colorScheme={colorScheme}>
            <ObjectInspector
              name={multiplayerStateManager.schema ? "state" : undefined}
              data={state}
              theme={theme}
            />
          </StateInspectorRow>
          {multiplayerStateManager.schema && (
            <StateInspectorRow colorScheme={colorScheme}>
              <ObjectInspector
                name="schema"
                data={multiplayerStateManager.schema}
                theme={theme}
              />
            </StateInspectorRow>
          )}
        </StateInspectorDisclosureRowInner>
      </StateInspectorDisclosureSection>
      <HistorySection
        showHistory={showHistory}
        setShowHistory={setShowHistory}
        colorScheme={colorScheme}
        historySnapshot={historySnapshot}
        multiplayerStateManager={multiplayerStateManager}
      />
      <StateInspectorDisclosureSection
        open={showAssets}
        setOpen={setShowAssets}
        title={
          <StateInspectorTitleLabel>
            Assets ({assets.length})
            <ColoredDot type={assetsInitialized ? "success" : "error"} />
          </StateInspectorTitleLabel>
        }
        colorScheme={colorScheme}
        right={
          <span style={{ display: "flex", gap: "10px" }}>
            <StateInspectorButton
              theme={theme}
              onClick={async () => {
                const ok = confirm(
                  "Are you sure you want to delete all assets?"
                );
                if (!ok) return;
                await Promise.all(
                  assets.map((asset) => assetManager.delete(asset.id))
                );
              }}
            >
              Delete all
            </StateInspectorButton>
            <StateInspectorButton
              theme={theme}
              onClick={() => {
                console.info(
                  "[StateInspector] Reloading assets",
                  noyaManager.id
                );
                assetManager.fetch();
              }}
            >
              Reload
            </StateInspectorButton>
            <StateInspectorButton
              theme={theme}
              onClick={async () => {
                const file = await uploadFile();
                assetManager.create(file);
              }}
            >
              Upload
            </StateInspectorButton>
            <StateInspectorButton
              theme={theme}
              onClick={async () => {
                const file = await uploadFile();
                assetManager.create({
                  data: new Uint8Array(await file.arrayBuffer()),
                  contentType: file.type,
                  mode: "mutable",
                });
              }}
            >
              Upload Mutable
            </StateInspectorButton>
          </span>
        }
      >
        <StateInspectorDisclosureRowInner>
          {assets.map((asset) => (
            <StateInspectorRow key={asset.id} colorScheme={colorScheme}>
              <ObjectInspector
                name={asset.id}
                data={truncateAsset(asset)}
                theme={theme}
              />
              <div style={{ display: "flex", gap: "4px" }}>
                {asset.mode === "mutable" && (
                  <StateInspectorButton
                    theme={theme}
                    onClick={async () => {
                      const file = await uploadFile();
                      assetManager.update(asset.id, {
                        data: new Uint8Array(await file.arrayBuffer()),
                        contentType: file.type,
                      });
                    }}
                  >
                    Replace
                  </StateInspectorButton>
                )}
                <StateInspectorButton
                  theme={theme}
                  onClick={() => assetManager.delete(asset.id)}
                >
                  Delete
                </StateInspectorButton>
              </div>
            </StateInspectorRow>
          ))}
        </StateInspectorDisclosureRowInner>
      </StateInspectorDisclosureSection>
      {advanced && (
        <StateInspectorDisclosureSection
          title={
            <StateInspectorTitleLabel>
              Resources ({resources.length})
              <ColoredDot type={resourcesInitialized ? "success" : "error"} />
            </StateInspectorTitleLabel>
          }
          colorScheme={colorScheme}
          open={showResources}
          setOpen={setShowResources}
          right={
            <span style={{ display: "flex", gap: "10px" }}>
              <StateInspectorButton
                theme={theme}
                onClick={async () => {
                  const ok = confirm(
                    "Are you sure you want to delete all resources?"
                  );
                  if (!ok) return;
                  await resourceManager.deleteResource(
                    resources.map((resource) => ({ id: resource.id }))
                  );
                }}
              >
                Delete all
              </StateInspectorButton>
              <StateInspectorButton
                theme={theme}
                onClick={async () => {
                  const path = prompt("Enter directory path") || uuid();
                  await resourceManager.createResource({
                    type: "directory",
                    path,
                  });
                }}
              >
                Create Directory
              </StateInspectorButton>
              <StateInspectorButton
                theme={theme}
                onClick={async () => {
                  const file = await uploadFile();

                  resourceManager.createResource({
                    type: "asset",
                    path: file.name || uuid(),
                    asset: {
                      content: Base64.encode(await file.arrayBuffer()),
                      contentType: file.type,
                      encoding: "base64",
                    },
                  });
                }}
              >
                Create Asset
              </StateInspectorButton>
            </span>
          }
        >
          <StateInspectorDisclosureRowInner>
            {resources?.map((resource) => (
              <StateInspectorRow key={resource.id} colorScheme={colorScheme}>
                <ObjectInspector
                  name={resource.path}
                  data={resource}
                  theme={theme}
                />
                <div style={{ display: "flex", gap: "4px" }}>
                  <StateInspectorButton
                    theme={theme}
                    onClick={() =>
                      resourceManager.deleteResource({ id: resource.id })
                    }
                  >
                    Delete
                  </StateInspectorButton>
                  <StateInspectorButton
                    theme={theme}
                    onClick={() => {
                      const path = prompt("Enter new path", resource.path);
                      if (!path) return;
                      resourceManager.updateResource({ id: resource.id, path });
                    }}
                  >
                    Rename
                  </StateInspectorButton>
                </div>
              </StateInspectorRow>
            ))}
          </StateInspectorDisclosureRowInner>
        </StateInspectorDisclosureSection>
      )}
      {advanced && (
        <StateInspectorDisclosureSection
          title={
            <StateInspectorTitleLabel>
              Secrets ({secrets.length})
              <ColoredDot type={secretsInitialized ? "success" : "error"} />
            </StateInspectorTitleLabel>
          }
          colorScheme={colorScheme}
          open={showSecrets}
          setOpen={setShowSecrets}
          right={
            <span style={{ display: "flex", gap: "10px" }}>
              <StateInspectorButton
                theme={theme}
                onClick={() => {
                  void secretManager.fetchSecrets();
                }}
              >
                Refresh
              </StateInspectorButton>
              <StateInspectorButton
                theme={theme}
                onClick={() => {
                  const name = prompt("Enter secret name");
                  if (!name) return;
                  const value = prompt("Enter secret value");
                  if (!value) return;
                  secretManager.createSecret(null, name, value);
                }}
              >
                Create
              </StateInspectorButton>
            </span>
          }
        >
          <StateInspectorDisclosureRowInner>
            {secrets.map((secret) => (
              <StateInspectorRow key={secret.id} colorScheme={colorScheme}>
                <ObjectInspector data={secret} theme={theme} />
                <StateInspectorButton
                  theme={theme}
                  onClick={() => secretManager.deleteSecret(secret.id)}
                >
                  Delete
                </StateInspectorButton>
              </StateInspectorRow>
            ))}
          </StateInspectorDisclosureRowInner>
        </StateInspectorDisclosureSection>
      )}
      {advanced && (
        <GitSection
          showGit={showGit}
          setShowGit={setShowGit}
          colorScheme={colorScheme}
          gitManager={gitManager}
        />
      )}
      {advanced && (
        <StateInspectorDisclosureSection
          open={showInputs}
          setOpen={setShowInputs}
          title={
            <StateInspectorTitleLabel>
              Inputs ({inputs.length})
              <ColoredDot type={inputsInitialized ? "success" : "error"} />
            </StateInspectorTitleLabel>
          }
          colorScheme={colorScheme}
        >
          <StateInspectorDisclosureRowInner>
            {inputs?.map((input: Input) => (
              <StateInspectorRow key={input.id} colorScheme={colorScheme}>
                <ObjectInspector data={input} theme={theme} />
              </StateInspectorRow>
            ))}
            {!inputs?.length && (
              <div
                style={{
                  padding: "12px",
                  fontSize: "12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span>No inputs</span>
              </div>
            )}
          </StateInspectorDisclosureRowInner>
        </StateInspectorDisclosureSection>
      )}
      {advanced && (
        <StateInspectorDisclosureSection
          open={showOutputTransforms}
          setOpen={setShowOutputTransforms}
          title={
            <StateInspectorTitleLabel>
              Output Transforms ({outputTransforms.length})
              <ColoredDot
                type={outputTransformsInitialized ? "success" : "error"}
              />
            </StateInspectorTitleLabel>
          }
          colorScheme={colorScheme}
        >
          <StateInspectorDisclosureRowInner>
            {outputTransforms?.map((transform: OutputTransform) => (
              <StateInspectorRow key={transform.id} colorScheme={colorScheme}>
                <ObjectInspector data={transform} theme={theme} />
              </StateInspectorRow>
            ))}
            {!outputTransforms?.length && (
              <div
                style={{
                  padding: "12px",
                  fontSize: "12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span>No output transforms</span>
              </div>
            )}
          </StateInspectorDisclosureRowInner>
        </StateInspectorDisclosureSection>
      )}
      {advanced && (
        <StateInspectorDisclosureSection
          title="Tasks"
          colorScheme={colorScheme}
          open={showTasks}
          setOpen={setShowTasks}
        >
          <StateInspectorDisclosureRowInner>
            {tasks?.map((task) => (
              <StateInspectorRow
                key={task.id}
                colorScheme={colorScheme}
                style={{
                  backgroundColor:
                    task.status === "done"
                      ? "rgba(0,255,0,0.2)"
                      : task.status === "error"
                        ? "rgba(255,0,0,0.2)"
                        : undefined,
                }}
              >
                <ObjectInspector
                  name={task.name}
                  data={task.payload}
                  theme={theme}
                />
              </StateInspectorRow>
            ))}
          </StateInspectorDisclosureRowInner>
        </StateInspectorDisclosureSection>
      )}
      <StateInspectorDisclosureSection
        open={showShared}
        setOpen={setShowShared}
        title="Shared Connection Data"
        colorScheme={colorScheme}
      >
        <StateInspectorDisclosureRowInner>
          {Object.entries(shared).map(([key, value]) => (
            <StateInspectorRow key={key} colorScheme={colorScheme}>
              <ObjectInspector
                name={key}
                data={value}
                theme={theme}
                expandLevel={10}
              />
            </StateInspectorRow>
          ))}
        </StateInspectorDisclosureRowInner>
      </StateInspectorDisclosureSection>
      <ServerScriptLogsSection
        colorScheme={colorScheme}
        showLogs={showServerLogs}
        setShowLogs={setShowServerLogs}
        logs={visibleServerLogs}
        paused={serverLogsPaused}
        onTogglePaused={toggleServerLogsPaused}
        containerRef={serverLogsContainerRef}
      />
      {showSimulationLogsSection && (
        <ServerScriptLogsSection
          title="Server Script Simulation Logs"
          colorScheme={colorScheme}
          showLogs={showServerSimulationLogs}
          setShowLogs={setShowServerSimulationLogs}
          logs={visibleServerSimulationLogs}
          paused={serverSimulationLogsPaused}
          onTogglePaused={toggleServerSimulationLogsPaused}
          containerRef={serverSimulationLogsContainerRef}
        />
      )}
      <StateInspectorDisclosureSection
        open={showSimulation}
        setOpen={setShowSimulation}
        title="Server Simulation"
        colorScheme={colorScheme}
      >
        <StateInspectorDisclosureRowInner>
          <StateInspectorRow colorScheme={colorScheme}>
            <ObjectInspector
              name="status"
              data={
                simulationDiagnostics ?? {
                  simulateLocally: false,
                  simulatorActive: false,
                  clockSkewMs: 0,
                  invocations: [],
                  pendingPredictions: [],
                }
              }
              theme={theme}
              expandLevel={2}
            />
          </StateInspectorRow>
        </StateInspectorDisclosureRowInner>
      </StateInspectorDisclosureSection>
      {advanced && (
        <ActivityEventsSection
          colorScheme={colorScheme}
          activityEventsContainerRef={activityEventsContainerRef}
          showEvents={showActivityEvents}
          setShowEvents={setShowActivityEvents}
          activityEventsManager={activityEventsManager}
        />
      )}
      <EventsSection<S>
        connectionEvents={visibleConnectionEvents}
        paused={connectionEventsPaused}
        onTogglePaused={toggleConnectionEventsPaused}
        colorScheme={colorScheme}
        eventsContainerRef={eventsContainerRef}
        showEvents={showEvents}
        setShowEvents={setShowEvents}
      />
    </div>
  );
});

const truncateAsset = memoize((asset: NoyaAsset) => {
  if (asset.url.startsWith("data:") || asset.url.startsWith("blob:")) {
    return { ...asset, url: ellipsis(asset.url, 40) };
  }
  return asset;
});
